import { useMemo } from "react"
import dayjs from "dayjs"
import styled from "styled-components"

import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { ApiTypes } from "types"
import ProductType from "types/enums/ProductType"
import thermostatV2Image from "assets/img/thermostat_v2.png"
import thsImage from "assets/img/ths.png"
import wlsImage from "assets/img/wls.png"
import { ReactComponent as IconDevice } from "assets/svg/device.svg"
import { ReactComponent as IconInfo } from "assets/svg/icon-info-message.svg"
import { ReactComponent as WireAdapterIcon } from "assets/svg/wire_adapter.svg"

import { getProductTypeLabel } from "utils/getProductTypeLabel"
import { CheckboxNew } from "components/form/CheckboxNew"
import { InputNumber } from "components/form/InputNumber"

import styles from "./index.module.scss"

const QuantityContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 4px;

  > span {
    font-size: 12px;
    color: #94a3b8;
  }
`

const HintText = styled.span`
  display: flex;
  font-size: 12px;
  margin-top: 4px;
  color: #94a3b8;
  align-items: center;
  gap: 3px;

  svg {
    flex-shrink: 0;

    path {
      fill: #94a3b8;
    }
  }
`

const DeviceWrapper = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: rgb(248, 250, 252);
  border-radius: 8px;
  padding: 12px 16px;
  opacity: ${props => (props.isSelected ? 1 : 0.7)};
`

const DeviceContent = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  font-family: inherit;
  pointer-events: ${props => (props.isSelected ? "auto" : "none")};
`

const BonusCWAQuantityContainer = styled.div`
  display: flex;
  align-items: center;
`

const BonusCWAQuantity = styled.div`
  display: flex;
  gap: 3px;
  font-size: 14px;
  color: #0f172a;
  margin-top: 8px;
  background-color: #ebfcff;
  padding: 10px 25px 10px 12px;
  border-radius: 10px;
  user-select: none;

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;

    path {
      fill: #2db2c8;
    }
  }
`

interface DeviceType {
  type: ApiTypes.Model.OrderProductType
  label: string
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  image?: string
  minQuantity: number
}

const buildDeviceTypes = (showV2: boolean, showSensors: boolean): DeviceType[] => {
  const useContractorOverride = !showV2
  const all: DeviceType[] = [
    {
      type: ProductType.Thermostat,
      label: getProductTypeLabel(ProductType.Thermostat, useContractorOverride)!,
      icon: IconDevice,
      minQuantity: 20,
    },
    {
      type: ProductType.ThermostatV2,
      label: getProductTypeLabel(ProductType.ThermostatV2)!,
      image: thermostatV2Image,
      minQuantity: 20,
    },
    {
      type: ProductType.CWA,
      label: getProductTypeLabel(ProductType.CWA)!,
      icon: WireAdapterIcon,
      minQuantity: 1,
    },
    {
      type: ProductType.THS,
      label: getProductTypeLabel(ProductType.THS)!,
      image: thsImage,
      minQuantity: 20,
    },
    {
      type: ProductType.WLS,
      label: getProductTypeLabel(ProductType.WLS)!,
      image: wlsImage,
      minQuantity: 20,
    },
  ]
  return all.filter(device => {
    if (!showV2 && device.type === ProductType.ThermostatV2) return false
    if (!showSensors && (device.type === ProductType.THS || device.type === ProductType.WLS)) return false
    return true
  })
}

const DeviceImage = styled.img<{ $padding?: string }>`
  width: 64px;
  height: 64px;
  padding: ${({ $padding }) => $padding ?? "12px"};
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  object-fit: contain;
  flex-shrink: 0;
`

interface OrdersModalContentProps {
  isNewOrder?: boolean
  selectedDevices: Record<ApiTypes.Model.OrderProductType, boolean>
  quantities: Record<ApiTypes.Model.OrderProductType, number>
  onDeviceToggle: (deviceType: ApiTypes.Model.OrderProductType) => void
  onQuantityChange: (deviceType: ApiTypes.Model.OrderProductType, quantity: number) => void
}

const OrdersModalContent = ({
  isNewOrder,
  selectedDevices,
  quantities,
  onDeviceToggle,
  onQuantityChange,
}: OrdersModalContentProps) => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const showV2 = useAppSelector(isFeatureEnabled(FeatureFlag.ThermV2Ordering))
  const showSensors = useAppSelector(isFeatureEnabled(FeatureFlag.SensorsOrdering))
  const deviceTypes = useMemo(() => buildDeviceTypes(showV2, showSensors), [showV2, showSensors])
  const thermostatQuantity = selectedDevices[ProductType.Thermostat] ? quantities.thermostat : 0
  const bonusCWAQuantity = Math.floor(thermostatQuantity / 20) * 5

  return (
    <div className={styles.modalContent}>
      <div className={styles.orderDetails}>
        <div className={styles.orderDetailsItem}>
          <span className={styles.orderDetailsLabel}>Contractor ID</span>
          <span className={styles.orderDetailsValue}>{userProfile?.contractor?.id}</span>
        </div>
        <div className={styles.orderDetailsItem}>
          <span className={styles.orderDetailsLabel}>Creation date</span>
          <span className={styles.orderDetailsValue}>{dayjs().format("L")}</span>
        </div>
        <div className={styles.orderDetailsItem}>
          <span className={styles.orderDetailsLabel}>Company name</span>
          <span className={styles.orderDetailsValue}>{userProfile?.contractor?.name}</span>
        </div>
      </div>
      <div className={styles.separator} />
      <div className={styles.devicesList}>
        {deviceTypes.map(device => {
          const Icon = device.icon
          const isSelected = selectedDevices[device.type]
          const quantity = quantities[device.type] || device.minQuantity
          const showMinQtyHint = device.type !== ProductType.CWA

          return (
            <DeviceWrapper key={device.type} isSelected={isSelected}>
              <div className={styles.deviceHeader}>
                <CheckboxNew
                  id={`device-${device.type}`}
                  isChecked={isSelected}
                  onChange={() => onDeviceToggle(device.type)}
                >
                  <span className={styles.deviceName}>{device.label}</span>
                </CheckboxNew>
              </div>
              <DeviceContent isSelected={isSelected}>
                {device.image ? (
                  <DeviceImage
                    src={device.image}
                    alt={device.label}
                    $padding={device.type === ProductType.ThermostatV2 ? "5px" : undefined}
                  />
                ) : (
                  Icon && <Icon />
                )}
                <div className={styles.deviceQtyWrapper}>
                  <span className={styles.deviceQty}>Qty: {quantity}</span>
                  {showMinQtyHint && (
                    <HintText>
                      <IconInfo />
                      Min Qty: {device.minQuantity}. For less contact Nuve team.
                    </HintText>
                  )}
                </div>
                <QuantityContainer>
                  <InputNumber
                    minValue={device.minQuantity}
                    onInputChange={value => onQuantityChange(device.type, value)}
                    disabled={!isSelected}
                    value={quantity}
                    dataTestId={`quantity-${device.type}`}
                  />
                </QuantityContainer>
              </DeviceContent>
              {device.type === ProductType.CWA && isSelected && bonusCWAQuantity > 0 && (
                <BonusCWAQuantityContainer>
                  <BonusCWAQuantity>
                    <IconInfo />
                    {Math.min(bonusCWAQuantity, quantity)} CWAs will be provided for free as part of your order.
                  </BonusCWAQuantity>
                </BonusCWAQuantityContainer>
              )}
            </DeviceWrapper>
          )
        })}
      </div>
      {isNewOrder && (
        <div className={styles.orderNote}>
          The recently placed order will be visible in the Orders list under the &ldquo;NEW&rdquo; status. You can
          monitor any changes to your order&apos;s status through Orders list.
        </div>
      )}
    </div>
  )
}

export default OrdersModalContent
